import { useEffect, useState, useCallback } from 'react';
import {
  adGdprSignal,
  isGranted,
  onConsentChange,
  whenConsentReady,
  openCookieSettings,
  openCookieBanner,
} from '@/lib/consent';
import { resolveAdUrl } from '@/lib/ad-url';

/**
 * Consent state for React components. `ready` flips once Zaraz's Consent API
 * is available (or after a short timeout if Zaraz is not on the page).
 * `signal` is the GDPR query fragment for ad requests and changes when the
 * visitor updates their choices.
 */
export function useConsent() {
  const [ready, setReady] = useState(false);
  const [signal, setSignal] = useState<string>('gdpr=1&gdpr_consent=');

  useEffect(() => {
    let alive = true;
    whenConsentReady().then(() => {
      if (!alive) return;
      setSignal(adGdprSignal());
      setReady(true);
    });
    const off = onConsentChange(() => {
      if (alive) setSignal(adGdprSignal());
    });
    return () => {
      alive = false;
      off();
    };
  }, []);

  const granted = useCallback((purposeName: string) => isGranted(purposeName), []);

  return { ready, signal, granted, openCookieSettings, openCookieBanner };
}

/**
 * Turns a raw ad tag URL (with `${GDPR}` macros) into the URL to load for this
 * visitor. Returns `null` until consent state is known so that the first ad
 * request already carries the right signal. Re-resolves when consent changes.
 */
export function useAdUrl(rawUrl: string | undefined): string | null {
  const { ready, signal } = useConsent();
  if (!ready || !rawUrl) return null;
  return resolveAdUrl(rawUrl, signal);
}
